import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  Car, 
  Users, 
  MapPin, 
  Clock, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  AlertCircle,
  CheckCircle,
  XCircle,
  Truck
} from "lucide-react";

const TripsStatusReport = () => {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareRoutes, setRideshareRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripsRes, driversRes, customersRes, rideshareRequestsRes, rideshareRoutesRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [tripsData, driversData, customersData, rideshareRequestsData, rideshareRoutesData] = await Promise.all([
        tripsRes.json(),
        driversRes.json(),
        customersRes.json(),
        rideshareRequestsRes.json(),
        rideshareRoutesRes.json()
      ]);

      setTrips(tripsData || []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareRoutes(Array.isArray(rideshareRoutesData) ? rideshareRoutesData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process trips data
  const tripsAnalysis = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    const filteredTrips = filterTripsByDate(trips, dateRange);

    // Rideshare revenue lives on `rideshare_requests` rows (one per passenger), not on `/trip`.
    // Join each request back to its route (`rideshare_trips`) to get the driver, and filter by
    // `created_at` since requests don't have `order_start_datetime`.
    const driverIdByRideshareId = {};
    rideshareRoutes.forEach(route => {
      driverIdByRideshareId[route.rideshare_id] = route.driver_id;
    });

    const filteredRideshareRequests = dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests;

    // Categorize trips by type and status
    const categorized = {
      private: {
        total: 0,
        statuses: {},
        statusRevenue: {},
        revenue: 0,
        avgRevenue: 0
      },
      rideshare: {
        total: 0,
        statuses: {},
        statusRevenue: {},
        revenue: 0,
        avgRevenue: 0
      },
      unknown: {
        total: 0,
        statuses: {},
        statusRevenue: {},
        revenue: 0,
        avgRevenue: 0
      }
    };

    // Overall status counts
    const statusCounts = {};
    const statusRevenue = {};

    const accumulate = (tripType, status, revenue) => {
      // Update overall status counts
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      statusRevenue[status] = (statusRevenue[status] || 0) + revenue;

      // Update categorized data
      categorized[tripType].total++;
      categorized[tripType].statuses[status] = (categorized[tripType].statuses[status] || 0) + 1;
      categorized[tripType].statusRevenue[status] = (categorized[tripType].statusRevenue[status] || 0) + revenue;
      categorized[tripType].revenue += revenue;
    };

    // `/trip` is exclusively the private-ride/delivery table — every row here is a private trip.
    filteredTrips.forEach(trip => {
      const revenue = parseFloat(trip.delivery_cost_proposed) || 0;
      const status = trip.status || 'Unknown';
      accumulate('private', status, revenue);
    });

    filteredRideshareRequests.forEach(req => {
      const revenue = parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0;
      const status = req.status || 'Unknown';
      accumulate('rideshare', status, revenue);
    });

    // Calculate averages
    Object.keys(categorized).forEach(type => {
      if (categorized[type].total > 0) {
        categorized[type].avgRevenue = categorized[type].revenue / categorized[type].total;
      }
    });

    return {
      filteredTrips,
      filteredRideshareRequests,
      categorized,
      statusCounts,
      statusRevenue,
      totalTrips: filteredTrips.length + filteredRideshareRequests.length,
      totalRevenue:
        filteredTrips.reduce((sum, trip) => sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0) +
        filteredRideshareRequests.reduce((sum, req) => sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0)
    };
  }, [trips, rideshareRequests, rideshareRoutes, timeFilter]);

  // Get status color and icon
  const getStatusInfo = (status) => {
    const statusMap = {
      'Completed': { color: 'green', icon: CheckCircle, label: 'Completed' },
      'InTransit': { color: 'blue', icon: Activity, label: 'In Transit' },
      'New Order': { color: 'yellow', icon: Clock, label: 'New Order' },
      'Accepted': { color: 'purple', icon: Users, label: 'Accepted' },
      'Cancelled': { color: 'red', icon: XCircle, label: 'Cancelled' },
      'Rejected': { color: 'red', icon: XCircle, label: 'Rejected' },
      'Expired': { color: 'orange', icon: AlertCircle, label: 'Expired' },
      'Waiting Driver Rating': { color: 'indigo', icon: Clock, label: 'Waiting Rating' },
      'Waiting customer to end trip': { color: 'cyan', icon: Clock, label: 'Waiting Customer' }
    };
    return statusMap[status] || { color: 'gray', icon: AlertCircle, label: status || 'Unknown' };
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename) => {
    const csv = [
      ['Status', 'Count', 'Revenue', 'Average Revenue'],
      ...data.map(item => [
        item.status,
        item.count,
        item.revenue.toFixed(2),
        item.avgRevenue.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const { filteredTrips, categorized, statusCounts, statusRevenue, totalTrips, totalRevenue } = tripsAnalysis;

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Trips Status Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive trip analysis by status and service type
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Trips {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalTrips}
                </p>
              </div>
              <Activity className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Revenue {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${totalRevenue.toFixed(0)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Private Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {categorized.private.total}
                </p>
              </div>
              <Car className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Rideshare Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {categorized.rideshare.total}
                </p>
              </div>
              <Users className="text-orange-500" size={24} />
            </div>
          </div>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Overall Status Breakdown */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <BarChart3 className="mr-2 text-blue-500" />
              Overall Status Breakdown
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(statusCounts).map(([status, count]) => {
                    const statusInfo = getStatusInfo(status);
                    const revenue = statusRevenue[status] || 0;
                    const avgRevenue = count > 0 ? revenue / count : 0;
                    
                    return (
                      <div key={status} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <statusInfo.icon className={`text-${statusInfo.color}-500`} size={20} />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {statusInfo.label}
                            </span>
                          </div>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                            {count}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${revenue.toFixed(2)}</p>
                          <p>Avg: ${avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Private Trips Analysis */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Car className="mr-2 text-blue-500" />
              Private Trips Analysis
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{categorized.private.total}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">${categorized.private.revenue.toFixed(0)}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">${categorized.private.avgRevenue.toFixed(2)}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Average Revenue</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(categorized.private.statuses).map(([status, count]) => {
                    const statusInfo = getStatusInfo(status);
                    return (
                      <div key={status} className="border rounded-lg p-3 dark:border-gray-700">
                        <div className="flex items-center space-x-2">
                          <statusInfo.icon className={`text-${statusInfo.color}-500`} size={16} />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {statusInfo.label}
                          </span>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            {count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Rideshare Trips Analysis */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Users className="mr-2 text-orange-500" />
              Rideshare Trips Analysis
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-orange-600">{categorized.rideshare.total}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">${categorized.rideshare.revenue.toFixed(0)}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">${categorized.rideshare.avgRevenue.toFixed(2)}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Average Revenue</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(categorized.rideshare.statuses).map(([status, count]) => {
                    const statusInfo = getStatusInfo(status);
                    // Revenue for this specific status among rideshare requests
                    const statusRevenue = categorized.rideshare.statusRevenue[status] || 0;
                    const statusAvgRevenue = count > 0 ? statusRevenue / count : 0;

                    return (
                      <div key={status} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <statusInfo.icon className={`text-${statusInfo.color}-500`} size={20} />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {statusInfo.label}
                            </span>
                          </div>
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
                            {count}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${statusRevenue.toFixed(2)}</p>
                          <p>Avg: ${statusAvgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TripsStatusReport;
